/**
 * Keeps the in-car "Recents" list in sync with the user's elefa call
 * history.
 *
 * Writes the top 20 unique destinations to Capacitor Preferences key
 *   `elefa.car.recents`
 * which is read natively by:
 *   - Android: tel.elefa.car.ElefaCarAppService (SharedPreferences "CapacitorStorage")
 *   - iOS:     ElefaCarPlaySceneDelegate (UserDefaults "CapacitorStorage.elefa.car.recents")
 *
 * Single writer, two readers — no platform-specific JS branches. Runs
 * only on native (web users have no car surface). Re-runs whenever
 * react-query refetches call history so a fresh call shows on the head
 * unit on next car launch without restarting the app.
 *
 * Privacy: we deliberately store ONLY (name, e164) — no costs, no
 * timestamps, no internal IDs. The car host UI does not need them and
 * SharedPreferences/UserDefaults are not encrypted at rest.
 */
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { useCallHistory, type CallRecord } from "@/hooks/useCallHistory";

const CAR_RECENTS_KEY = "elefa.car.recents";
const MAX_ENTRIES = 20;

interface CarRecent {
  name: string;
  e164: string;
}

function toE164(raw: string | null | undefined): string {
  if (!raw) return "";
  const cleaned = raw.replace(/[^\d+]/g, "");
  if (/^\+\d{6,15}$/.test(cleaned)) return cleaned;
  // History sometimes stores bare digits — best-effort upgrade.
  if (/^\d{6,15}$/.test(cleaned)) return `+${cleaned}`;
  return "";
}

function buildEntries(calls: CallRecord[]): CarRecent[] {
  const seen = new Set<string>();
  const out: CarRecent[] = [];
  for (const c of calls) {
    const e164 = toE164(c.destination_number);
    if (!e164 || seen.has(e164)) continue;
    seen.add(e164);
    const name =
      (c.destination_country && c.destination_country.trim()) ||
      e164;
    out.push({ name, e164 });
    if (out.length >= MAX_ENTRIES) break;
  }
  return out;
}

export default function useCarRecentsSync() {
  // Pull the first page (20 rows) — same size we publish to the car.
  const { calls } = useCallHistory(0, MAX_ENTRIES);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!Array.isArray(calls)) return;

    const entries = buildEntries(calls);
    const payload = JSON.stringify(entries);

    // Fire-and-forget; failures here are non-fatal (car UI degrades to
    // empty state with "place a call from your phone to populate").
    Preferences.set({ key: CAR_RECENTS_KEY, value: payload }).catch(() => {
      /* swallow — not user-visible */
    });
  }, [calls]);
}
